/**
 * model-assignment-store.ts
 *
 * Model Assignment Store, Hierarchical Scope Resolution
 * (channel > agent > service > node > tenant > global), Replica Bounds,
 * and Cross-Model Fallback Protection.
 */

import {
  ModelAssignment,
  AssignmentScope,
  ModelKind,
  PolicyError,
} from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';

export const SCOPE_PRECEDENCE: readonly AssignmentScope[] = [
  'channel',
  'agent',
  'service',
  'node',
  'tenant',
  'global',
];

export class ModelAssignmentStore {
  constructor(private readonly store: OrchestrationStore) {}

  async createAssignment(assignment: ModelAssignment): Promise<ModelAssignment> {
    if (
      assignment.minReplicas !== undefined &&
      assignment.maxReplicas !== undefined &&
      assignment.minReplicas > assignment.maxReplicas
    ) {
      throw new PolicyError(
        `Invalid assignment bounds: minReplicas (${assignment.minReplicas}) cannot exceed maxReplicas (${assignment.maxReplicas})`
      );
    }

    await this.store.saveAssignment(assignment);
    await this.store.appendEvent({
      eventId: `evt-assign-create-${Date.now()}-${assignment.id}`,
      kind: 'assignment.created',
      payload: { id: assignment.id, scope: assignment.scope, scopeId: assignment.scopeId, modelKind: assignment.modelKind },
      originId: 'assignment-store',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return assignment;
  }

  async updateAssignment(id: string, patch: Partial<ModelAssignment>): Promise<ModelAssignment> {
    const existing = (await this.store.getAssignments()).find((a) => a.id === id);
    if (!existing) {
      throw new PolicyError(`Assignment ${id} not found`);
    }

    const updated: ModelAssignment = {
      ...existing,
      ...patch,
    };

    if (
      updated.minReplicas !== undefined &&
      updated.maxReplicas !== undefined &&
      updated.minReplicas > updated.maxReplicas
    ) {
      throw new PolicyError(
        `Invalid assignment bounds: minReplicas (${updated.minReplicas}) cannot exceed maxReplicas (${updated.maxReplicas})`
      );
    }

    await this.store.saveAssignment(updated);
    await this.store.appendEvent({
      eventId: `evt-assign-update-${Date.now()}-${id}`,
      kind: 'assignment.updated',
      payload: { id, patch },
      originId: 'assignment-store',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return updated;
  }

  async deleteAssignment(id: string): Promise<void> {
    await this.store.deleteAssignment(id);
    await this.store.appendEvent({
      eventId: `evt-assign-del-${Date.now()}-${id}`,
      kind: 'assignment.deleted',
      payload: { id },
      originId: 'assignment-store',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });
  }

  async getAllAssignments(): Promise<readonly ModelAssignment[]> {
    return this.store.getAssignments();
  }

  /**
   * Resolves the highest-priority enabled assignment for a given query,
   * walking down the scope precedence hierarchy (channel > agent > service > node > tenant > global).
   */
  async resolveAssignment(
    scope: AssignmentScope,
    scopeId: string,
    modelKind: ModelKind
  ): Promise<ModelAssignment | undefined> {
    const all = await this.store.getAssignments();
    const enabled = all.filter((a) => a.enabled && a.modelKind === modelKind);

    // 1. Direct exact scope match
    const exactMatches = enabled
      .filter((a) => a.scope === scope && (a.scopeId === scopeId || a.scope === 'global'))
      .sort((a, b) => b.priority - a.priority);

    if (exactMatches.length > 0) {
      return exactMatches[0];
    }

    // 2. Hierarchical fallback search
    for (const s of SCOPE_PRECEDENCE) {
      const candidates = enabled
        .filter((a) => a.scope === s && (a.scope === 'global' || a.scopeId === scopeId))
        .sort((a, b) => b.priority - a.priority);

      if (candidates.length > 0) {
        return candidates[0];
      }
    }

    return undefined;
  }
}
